import { ErrorMessage, Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import users from "../../apiConfigs/users";
import jobs from "../../apiConfigs/jobs";
import properties from "../../apiConfigs/properties";
import Select from "react-select";
import { countriesData } from "../../utils/countries";
import { ReactComponent as Seperate } from "../../assets/seperator.svg";
import Button from "../../designComponents/Button";
import Dropdown from "../../designComponents/Dropdown/Dropdown";
import LabelInputField from "../../designComponents/LabelInputField/LabelInputField";
import MediaUploader from "../../designComponents/MediaUpload/MediaUpload";
import Typography from "../../designComponents/Typography";
import requestAPI from "../../utils/requestAPI";
import { uploadFilesToS3OnlyKeys } from "../../utils/s3Upload";
import {
  ActiveNavText,
  Navigation,
  NavLink,
  NavText,
  Title,
  Wrapper,
} from "../Properties/Details/styles";

const countryOptions = countriesData
  .filter((c, index, self) => {
    const code = c.dial_code.trim();
    return self.findIndex((x) => x.dial_code.trim() === code) === index;
  })
  .map((country) => ({
    label: `${country.flag} ${country.dial_code.trim()}`,
    value: country.dial_code.trim(),
  }));

const formatRtbNumber = (text: string) => {
  if (!text) return "";
  let clean = text.replace(/-/g, "").toUpperCase();

  // Enforce RT prefix on the fly
  if (!clean.startsWith("RT")) {
    if (clean.startsWith("R")) {
      clean = "R" + clean.slice(1).replace(/[^A-Z0-9]/g, "");
    } else {
      clean = "RT" + clean.replace(/[^A-Z0-9]/g, "");
    }
  } else {
    clean = "RT" + clean.slice(2).replace(/[^A-Z0-9]/g, "");
  }

  if (clean.length <= 2) {
    return clean;
  }
  if (clean.length <= 6) {
    return `${clean.slice(0, 2)}-${clean.slice(2)}`;
  }
  return `${clean.slice(0, 2)}-${clean.slice(2, 6)}-${clean.slice(6, 14)}`;
};

const tenantRoleOptions = [
  { label: "Tenant Home Seeker", value: "HOME_SEEKER" },
  { label: "Student", value: "STUDENT" },
];

const purposeOptions = [
  { label: "Rent a Property", value: "rent-a-property" },
  { label: "Create a Job", value: "create-a-property" },
  { label: "Marketplace Products", value: "marketplace-products" },
  { label: "Browse Property", value: "browse-property" },
  { label: "Search Service Provider", value: "search-service-provider" },
];

const roleOptions = [
  { label: "Landlord", value: "LANDLORD" },
  { label: "Tenant", value: "TENANT" },
  { label: "Service Provider", value: "SERVICE_PROVIDER" },
];

const accountTypeOptions = [
  { label: "Individual", value: "INDIVIDUAL" },
  { label: "Company", value: "COMPANY" },
  { label: "Partnership Trust Pension", value: "PARTNERSHIP_TRUST_PENSION" },
  { label: "Institutional Corporate", value: "INSTITUTIONAL_CORPORATE" },
];

const AddUser = () => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [tradeCategories, setTradeCategories] = useState<{ _id: string; title: string }[]>([]);
  const navigate = useNavigate();

  const [addressOptions, setAddressOptions] = useState<any[]>([]);
  const [addressInputValue, setAddressInputValue] = useState("");
  const [addressLoading, setAddressLoading] = useState(false);

  useEffect(() => {
    const fetchAddresses = async () => {
      if (addressInputValue.trim() === "") {
        setAddressOptions([]);
        return;
      }

      setAddressLoading(true);
      try {
        const response = await requestAPI(
          properties.getSearchProperty(addressInputValue)
        );
        const data = response;
        const formattedOptions = data.map((item: any) => ({
          label: item.description,
          value: item.description,
        }));

        setAddressOptions(formattedOptions);
      } catch (error) {
        console.error("Error fetching address suggestions:", error);
      } finally {
        setAddressLoading(false);
      }
    };

    const debounceFetch = setTimeout(() => fetchAddresses(), 300);
    return () => clearTimeout(debounceFetch);
  }, [addressInputValue]);

  useEffect(() => {
    const fetchTradeCategories = async () => {
      try {
        const response = await requestAPI(jobs.getTradeCategories());
        if (response && response.data) {
          setTradeCategories(response.data);
        }
      } catch (err: any) {
        console.error("Failed to load trade categories:", err);
      }
    };
    fetchTradeCategories();
  }, []);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required("First Name is required")
      .max(50, "First Name cannot exceed 50 characters"),
    lastName: Yup.string()
      .required("Last Name is required")
      .max(50, "Last Name cannot exceed 50 characters"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required")
      .max(100, "Email cannot exceed 100 characters"),
    countryCode: Yup.string()
      .required("Country Code is required")
      .matches(/^\+\d{1,4}$/, "Country code must start with + followed by 1 to 4 digits"),
    phoneNumber: Yup.string()
      .required("Phone Number is required")
      .matches(/^\d+$/, "Phone number must contain only digits")
      .min(5, "Phone number must be at least 5 digits")
      .max(15, "Phone number cannot exceed 15 digits"),
    address: Yup.string()
      .required("Address is required")
      .max(250, "Address cannot exceed 250 characters"),
    userRole: Yup.string().required("Role is required"),
    accountType: Yup.string().required("Account type is required"),
    
    // Landlord validations
    propertiesOwned: Yup.number().when("userRole", {
      is: "LANDLORD",
      then: (schema) => schema.required("Properties Owned is required").min(0, "Must be at least 0").max(1000000, "Must be a reasonable number"),
      otherwise: (schema) => schema.notRequired(),
    }),
    RTB: Yup.string().when("userRole", {
      is: "LANDLORD",
      then: (schema) =>
        schema
          .test(
            "rtb-format",
            "RTB number must strictly match layout RT-XXXX-XXXXXXXX",
            (value) => !value || /^(RT|rt|Rt)-\w{4}-\w{8}$/.test(value)
          )
          .nullable()
          .notRequired(),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Landlord Company validations
    companyName: Yup.string().when(["userRole", "accountType"], {
      is: (userRole: string, accountType: string) => userRole === "LANDLORD" && accountType !== "INDIVIDUAL",
      then: (schema) => schema.required("Company Name is required").max(100, "Company Name cannot exceed 100 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    croNumber: Yup.string().when(["userRole", "accountType"], {
      is: (userRole: string, accountType: string) => userRole === "LANDLORD" && accountType !== "INDIVIDUAL",
      then: (schema) => schema.required("CRO Number is required").max(20, "CRO Number cannot exceed 20 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    registeredAddress: Yup.string().when(["userRole", "accountType"], {
      is: (userRole: string, accountType: string) => userRole === "LANDLORD" && accountType !== "INDIVIDUAL",
      then: (schema) => schema.required("Registered Address is required").max(250, "Registered Address cannot exceed 250 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    companyWebsite: Yup.string()
      .nullable()
      .notRequired()
      .transform((v, o) => (o === "" ? null : v))
      .url("Invalid URL")
      .max(100, "Website URL cannot exceed 100 characters"),
    companyContactName: Yup.string().when(["userRole", "accountType"], {
      is: (userRole: string, accountType: string) => userRole === "LANDLORD" && accountType !== "INDIVIDUAL",
      then: (schema) => schema.required("Contact Name is required").max(100, "Contact Name cannot exceed 100 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    companyContactEmail: Yup.string().when(["userRole", "accountType"], {
      is: (userRole: string, accountType: string) => userRole === "LANDLORD" && accountType !== "INDIVIDUAL",
      then: (schema) => schema.email("Invalid email").required("Contact Email is required").max(100, "Contact Email cannot exceed 100 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    companyContactPhone: Yup.string().when(["userRole", "accountType"], {
      is: (userRole: string, accountType: string) => userRole === "LANDLORD" && accountType !== "INDIVIDUAL",
      then: (schema) =>
        schema
          .required("Contact Phone is required")
          .matches(/^\d+$/, "Phone number must contain only digits")
          .min(5, "Phone number must be at least 5 digits")
          .max(15, "Phone number cannot exceed 15 digits"),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Tenant validations
    subRole: Yup.string().when("userRole", {
      is: "TENANT",
      then: (schema) => schema.required("Tenant type is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    purposes: Yup.array().when("userRole", {
      is: "TENANT",
      then: (schema) => schema.min(1, "Select at least one purpose"),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Service Provider validations
    serviceProviderCategories: Yup.array().when("userRole", {
      is: "SERVICE_PROVIDER",
      then: (schema) => schema.min(1, "Select at least one category"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleSubmit = async (values: any) => {
    let providedUrl = "";

    try {
      if (uploadedFile && typeof uploadedFile !== "string") {
        const uploadFiles = await uploadFilesToS3OnlyKeys({
          category: "avatar",
          fileUri: [uploadedFile],
          userId: "new",
        });
        if (uploadFiles && uploadFiles.length > 0) {
          providedUrl = uploadFiles[0];
        }
      }

      const payload: any = {
        firstName: values.firstName,
        lastName: values.lastName,
        address: values.address,
        email: values.email,
        phone: values.phoneNumber,
        countryCode: values.countryCode,
        roles: [values.userRole],
        accountType: values.accountType,
        avatar: providedUrl || undefined,
      };

      if (values.userRole === "TENANT") {
        payload.subRole = values.subRole;
        payload.purposes = values.purposes;
        payload.desiredAddress = values.desiredAddress || undefined;
      } else if (values.userRole === "SERVICE_PROVIDER") {
        payload.serviceProviderCategories = values.serviceProviderCategories;
        if (values.vatNo) {
          payload.registrations = [
            {
              regType: "VAT",
              company: "Revenue",
              regNo: values.vatNo,
            },
          ];
        }
      } else if (values.userRole === "LANDLORD") {
        payload.propertiesOwned = Number(values.propertiesOwned);
        payload.RTB = values.RTB || undefined;
        if (values.accountType !== "INDIVIDUAL") {
          payload.companyDetails = {
            companyName: values.companyName,
            croNumber: values.croNumber,
            registeredAddress: values.registeredAddress,
            companyWebsite: values.companyWebsite || undefined,
            companyContact: {
              name: values.companyContactName,
              email: values.companyContactEmail,
              phone: values.companyContactPhone,
            },
          };
        }
      }

      await requestAPI(users.createUser(payload));
      toast.success("Successfully added user");
      navigate(-1);
    } catch (error: any) {
      toast.error("Error: " + (error.response?.data?.message || error.message));
    }
  };

  return (
    <Wrapper>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <Title style={{ paddingBottom: 14 }}>Add User</Title>
          <Navigation
            style={{
              paddingBottom: 15,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <NavLink onClick={() => navigate(-1)}>
              <NavText>Table</NavText>
            </NavLink>
            <Seperate fill="#7E7E7E"></Seperate>
            <ActiveNavText>Add User</ActiveNavText>
          </Navigation>
        </div>
        <div
          style={{
            display: "flex",
            gap: 20,
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Button
            style={{
              border: "1px solid #D8D8D8",
              background: "white",
              borderRadius: 8,
              color: "#7E7E7E",
            }}
            onClick={() => {
              navigate(-1);
            }}
          >
            Cancel
          </Button>
        </div>
      </div>

      <Formik
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          countryCode: "+353",
          phoneNumber: "",
          address: "",
          userRole: "LANDLORD",
          accountType: "INDIVIDUAL",
          subRole: "HOME_SEEKER",
          purposes: [] as string[],
          desiredAddress: "",
          serviceProviderCategories: [] as string[],
          vatNo: "",
          propertiesOwned: 0,
          RTB: "",
          companyName: "",
          croNumber: "",
          registeredAddress: "",
          companyWebsite: "",
          companyContactName: "",
          companyContactEmail: "",
          companyContactPhone: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched, isSubmitting }) => (
          <Form style={{ width: "100%", display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "flex", gap: 18, flexDirection: "row", flexWrap: "wrap" }}>
              {/* General & Personal Info Card */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 18,
                  flex: 1,
                  minWidth: 320,
                }}
              >
                <div
                  className="add-user-card"
                  style={{
                    backgroundColor: "white",
                    padding: 20,
                    borderRadius: 10,
                    border: "1px solid #F1EFFB",
                  }}
                >
                  <Typography fontWeight="medium" size={"h3"} style={{ marginBottom: 12 }}>
                    General Information
                  </Typography>

                  <div style={{ display: "flex", gap: 20, width: "100%" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "50%" }}>
                      <LabelInputField
                        onChange={(e) => setFieldValue("firstName", e.target.value)}
                        name="firstName"
                        placeHolder="e.g. Alfonso"
                        style={{
                          label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                          input: {
                            height: 50,
                            border: "1px solid #F1EFFB",
                            padding: 8,
                            background: "#F1EFFB",
                            borderRadius: 6,
                            width: "100%",
                          },
                          container: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 14, width: "100%" },
                        }}
                        label="First Name"
                      />
                      <ErrorMessage name="firstName">
                        {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                      </ErrorMessage>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "50%" }}>
                      <LabelInputField
                        onChange={(e) => setFieldValue("lastName", e.target.value)}
                        name="lastName"
                        placeHolder="e.g. Crist"
                        style={{
                          label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                          input: {
                            height: 50,
                            border: "1px solid #F1EFFB",
                            padding: 8,
                            background: "#F1EFFB",
                            borderRadius: 6,
                            width: "100%",
                          },
                          container: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 14, width: "100%" },
                        }}
                        label="Last Name"
                      />
                       <ErrorMessage name="lastName">
                         {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                       </ErrorMessage>
                    </div>
                  </div>

                  <LabelInputField
                    name="email"
                    onChange={(e) => setFieldValue("email", e.target.value)}
                    placeHolder="Alfonso.Crist09@gmail.com"
                    style={{
                      label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                      input: {
                        height: 50,
                        border: "1px solid #F1EFFB",
                        padding: 8,
                        background: "#F1EFFB",
                        borderRadius: 6,
                        width: "100%",
                      },
                      container: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 14 },
                    }}
                    label="Email"
                  />
                   <ErrorMessage name="email">
                     {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                   </ErrorMessage>
                </div>

                <div
                  className="add-user-card"
                  style={{
                    backgroundColor: "white",
                    padding: 20,
                    borderRadius: 10,
                    border: "1px solid #F1EFFB",
                  }}
                >
                  <Typography fontWeight="medium" size={"h3"} style={{ marginBottom: 12 }}>
                    Personal Information
                  </Typography>

                  <div style={{ display: "flex", gap: 20, width: "100%" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "35%", paddingTop: 14 }}>
                      <Typography fontFamily="urbanist" style={{ fontSize: "14px", fontWeight: "500", color: "#555A77" }}>
                        Country Code
                      </Typography>
                      <Select
                        name="countryCode"
                        options={countryOptions}
                        onChange={(option: any) => setFieldValue("countryCode", option ? option.value : "")}
                        value={
                          countryOptions.find((opt) => opt.value === values.countryCode) ||
                          (values.countryCode ? { label: values.countryCode, value: values.countryCode } : null)
                        }
                        placeholder="Select..."
                        isSearchable
                        styles={{
                          control: (baseStyles) => ({
                            ...baseStyles,
                            height: 50,
                            backgroundColor: "#F1EFFB",
                            border: "1px solid #F1EFFB",
                            borderRadius: 6,
                            boxShadow: "none",
                            fontFamily: "urbanist",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#555A77",
                            "&:hover": {
                              border: "1px solid #F1EFFB",
                            },
                          }),
                          singleValue: (baseStyles) => ({
                            ...baseStyles,
                            fontFamily: "urbanist",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#555A77",
                          }),
                          placeholder: (baseStyles) => ({
                            ...baseStyles,
                            fontFamily: "urbanist",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#a0a5ba",
                          }),
                          input: (baseStyles) => ({
                            ...baseStyles,
                            fontFamily: "urbanist",
                            fontSize: "14px",
                            fontWeight: 500,
                          }),
                          option: (baseStyles, state) => ({
                            ...baseStyles,
                            fontFamily: "urbanist",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: state.isSelected ? "white" : "#555A77",
                            backgroundColor: state.isSelected ? "#0A56AF" : state.isFocused ? "#E8F7FF" : "white",
                            "&:active": {
                              backgroundColor: "#0A56AF",
                            }
                          }),
                          menu: (baseStyles) => ({
                            ...baseStyles,
                            zIndex: 9999,
                          }),
                        }}
                      />
                       <ErrorMessage name="countryCode">
                         {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                       </ErrorMessage>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "65%" }}>
                      <LabelInputField
                        name="phoneNumber"
                        value={values.phoneNumber}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, "");
                          if (value.length > 15) {
                            value = value.slice(0, 15);
                          }
                          setFieldValue("phoneNumber", value);
                        }}
                        placeHolder="e.g. 830123456"
                        maxLength={15}
                        style={{
                          label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                          input: {
                            height: 50,
                            border: "1px solid #F1EFFB",
                            padding: 8,
                            background: "#F1EFFB",
                            borderRadius: 6,
                            width: "100%",
                          },
                          container: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 14, width: "100%" },
                        }}
                        label="Phone Number"
                      />
                       <ErrorMessage name="phoneNumber">
                         {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                       </ErrorMessage>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 14 }}>
                    <Typography fontFamily="urbanist" style={{ fontSize: "14px", fontWeight: "500", color: "#555A77" }}>
                      Address
                    </Typography>
                    <Select
                      name="address"
                      options={addressOptions}
                      onInputChange={(value) => setAddressInputValue(value)}
                      onChange={(option: any) => setFieldValue("address", option ? option.value : "")}
                      value={
                        addressOptions.find((opt) => opt.value === values.address) ||
                        (values.address ? { label: values.address, value: values.address } : null)
                      }
                      placeholder="Search Address..."
                      isClearable
                      isLoading={addressLoading}
                      filterOption={() => true}
                      noOptionsMessage={({ inputValue }) =>
                        addressLoading
                          ? "Loading suggestions..."
                          : !inputValue
                          ? "Start typing to search address..."
                          : "No options found"
                      }
                      styles={{
                        control: (baseStyles) => ({
                          ...baseStyles,
                          height: 50,
                          backgroundColor: "#F1EFFB",
                          border: "1px solid #F1EFFB",
                          borderRadius: 6,
                          boxShadow: "none",
                          fontFamily: "urbanist",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#555A77",
                          "&:hover": {
                            border: "1px solid #F1EFFB",
                          },
                        }),
                        singleValue: (baseStyles) => ({
                          ...baseStyles,
                          fontFamily: "urbanist",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#555A77",
                        }),
                        placeholder: (baseStyles) => ({
                          ...baseStyles,
                          fontFamily: "urbanist",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#a0a5ba",
                        }),
                        input: (baseStyles) => ({
                          ...baseStyles,
                          fontFamily: "urbanist",
                          fontSize: "14px",
                          fontWeight: 500,
                        }),
                        option: (baseStyles, state) => ({
                          ...baseStyles,
                          fontFamily: "urbanist",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: state.isSelected ? "white" : "#555A77",
                          backgroundColor: state.isSelected ? "#0A56AF" : state.isFocused ? "#E8F7FF" : "white",
                          "&:active": {
                            backgroundColor: "#0A56AF",
                          }
                        }),
                        menu: (baseStyles) => ({
                          ...baseStyles,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>
                   <ErrorMessage name="address">
                     {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                   </ErrorMessage>

                  <MediaUploader
                    label="Profile Picture"
                    setSelectedFiles={setUploadedFile}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setUploadedFile(file);
                    }}
                    style={{
                      container: { paddingTop: 14, gap: 6 },
                      label: { paddingBottom: 10, color: "#555A77" },
                      dropzone: { background: "#F1EFFB" },
                    }}
                  ></MediaUploader>
                </div>
              </div>

              {/* Roles, Account type, and Dynamic Settings Card */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 20,
                  width: 400,
                  minWidth: 320,
                }}
              >
                <div
                  style={{
                    background: "white",
                    borderRadius: 10,
                    padding: 20,
                    border: "1px solid #F1EFFB",
                  }}
                >
                  <Typography fontWeight="medium" size={"h3"} style={{ marginBottom: 12 }}>
                    Role & Account Configuration
                  </Typography>

                  <Dropdown
                    options={roleOptions}
                    label="Select User Role"
                    value={values.userRole}
                    style={{
                      container: { paddingTop: 10 },
                      label: { paddingBottom: 6, color: "#555A77" },
                      select: {
                        backgroundColor: "#F1EFFB",
                        fontSize: 14,
                        fontFamily: "urbanist",
                        fontWeight: 500,
                        padding: "8px",
                      },
                    }}
                    onChange={(e) => {
                      setFieldValue("userRole", e.target.value);
                    }}
                  ></Dropdown>
                   <ErrorMessage name="userRole">
                     {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                   </ErrorMessage>

                  <Dropdown
                    options={accountTypeOptions}
                    label="Select Account Type"
                    value={values.accountType}
                    style={{
                      container: { paddingTop: 14 },
                      label: { paddingBottom: 6, color: "#555A77" },
                      select: {
                        backgroundColor: "#F1EFFB",
                        fontSize: 14,
                        fontFamily: "urbanist",
                        fontWeight: 500,
                        padding: "8px",
                      },
                    }}
                    onChange={(e) => {
                      setFieldValue("accountType", e.target.value);
                    }}
                  ></Dropdown>
                   <ErrorMessage name="accountType">
                     {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                   </ErrorMessage>
                </div>

                {/* TENANT SPECIFIC FIELDS */}
                {values.userRole === "TENANT" && (
                  <div
                    style={{
                      background: "white",
                      borderRadius: 10,
                      padding: 20,
                      border: "1px solid #F1EFFB",
                      display: "flex",
                      flexDirection: "column",
                      gap: 14,
                    }}
                  >
                    <Typography fontWeight="medium" size={"h3"}>
                      Tenant Details
                    </Typography>

                    <Dropdown
                      options={tenantRoleOptions}
                      label="Select Tenant Type"
                      value={values.subRole}
                      style={{
                        container: { paddingTop: 6 },
                        label: { paddingBottom: 6, color: "#555A77" },
                        select: {
                          backgroundColor: "#F1EFFB",
                          fontSize: 14,
                          fontFamily: "urbanist",
                          fontWeight: 500,
                          padding: "8px",
                        },
                      }}
                      onChange={(e) => {
                        setFieldValue("subRole", e.target.value);
                      }}
                    ></Dropdown>
                     <ErrorMessage name="subRole">
                       {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                     </ErrorMessage>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <Typography style={{ fontSize: "14px", fontWeight: "500", color: "#555A77" }}>
                        Primary Purposes
                      </Typography>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, background: "#F1EFFB", padding: 12, borderRadius: 6 }}>
                        {purposeOptions.map((opt) => {
                          const isChecked = values.purposes.includes(opt.value);
                          return (
                            <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14 }}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  if (isChecked) {
                                    setFieldValue("purposes", values.purposes.filter((p) => p !== opt.value));
                                  } else {
                                    setFieldValue("purposes", [...values.purposes, opt.value]);
                                  }
                                }}
                              />
                              {opt.label}
                            </label>
                          );
                        })}
                      </div>
                       <ErrorMessage name="purposes">
                         {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                       </ErrorMessage>
                    </div>

                    <LabelInputField
                      name="desiredAddress"
                      onChange={(e) => setFieldValue("desiredAddress", e.target.value)}
                      placeHolder="e.g. Dublin South"
                      style={{
                        label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                        input: {
                          height: 50,
                          border: "1px solid #F1EFFB",
                          padding: 8,
                          background: "#F1EFFB",
                          borderRadius: 6,
                          width: "100%",
                        },
                        container: { display: "flex", flexDirection: "column", gap: 6 },
                      }}
                      label="Desired Address"
                    />
                  </div>
                )}

                {/* SERVICE PROVIDER SPECIFIC FIELDS */}
                {values.userRole === "SERVICE_PROVIDER" && (
                  <div
                    style={{
                      background: "white",
                      borderRadius: 10,
                      padding: 20,
                      border: "1px solid #F1EFFB",
                      display: "flex",
                      flexDirection: "column",
                      gap: 14,
                    }}
                  >
                    <Typography fontWeight="medium" size={"h3"}>
                      Service Provider Details
                    </Typography>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <Typography style={{ fontSize: "14px", fontWeight: "500", color: "#555A77" }}>
                        Trade Categories
                      </Typography>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, background: "#F1EFFB", padding: 12, borderRadius: 6, maxHeight: 200, overflowY: "auto" }}>
                        {tradeCategories.length > 0 ? (
                          tradeCategories.map((cat) => {
                            const isChecked = values.serviceProviderCategories.includes(cat._id);
                            return (
                              <label key={cat._id} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14 }}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {
                                    if (isChecked) {
                                      setFieldValue("serviceProviderCategories", values.serviceProviderCategories.filter((c) => c !== cat._id));
                                    } else {
                                      setFieldValue("serviceProviderCategories", [...values.serviceProviderCategories, cat._id]);
                                    }
                                  }}
                                />
                                {cat.title}
                              </label>
                            );
                          })
                        ) : (
                          <Typography style={{ fontSize: 12, color: "#7E7E7E" }}>Loading categories...</Typography>
                        )}
                      </div>
                       <ErrorMessage name="serviceProviderCategories">
                         {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                       </ErrorMessage>
                    </div>

                    <LabelInputField
                      name="vatNo"
                      onChange={(e) => setFieldValue("vatNo", e.target.value)}
                      placeHolder="IE1234567A"
                      style={{
                        label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                        input: {
                          height: 50,
                          border: "1px solid #F1EFFB",
                          padding: 8,
                          background: "#F1EFFB",
                          borderRadius: 6,
                          width: "100%",
                        },
                        container: { display: "flex", flexDirection: "column", gap: 6 },
                      }}
                      label="VAT Number (Optional)"
                    />
                  </div>
                )}

                {/* LANDLORD SPECIFIC FIELDS */}
                {values.userRole === "LANDLORD" && (
                  <div
                    style={{
                      background: "white",
                      borderRadius: 10,
                      padding: 20,
                      border: "1px solid #F1EFFB",
                      display: "flex",
                      flexDirection: "column",
                      gap: 14,
                    }}
                  >
                    <Typography fontWeight="medium" size={"h3"}>
                      Landlord Details
                    </Typography>

                    <LabelInputField
                      name="propertiesOwned"
                      onChange={(e) => setFieldValue("propertiesOwned", Number(e.target.value) || 0)}
                      placeHolder="e.g. 2"
                      style={{
                        label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                        input: {
                          height: 50,
                          border: "1px solid #F1EFFB",
                          padding: 8,
                          background: "#F1EFFB",
                          borderRadius: 6,
                          width: "100%",
                        },
                        container: { display: "flex", flexDirection: "column", gap: 6 },
                      }}
                      label="Properties Owned"
                    />
                     <ErrorMessage name="propertiesOwned">
                       {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                     </ErrorMessage>

                    <LabelInputField
                      name="RTB"
                      value={values.RTB}
                      onChange={(e) => setFieldValue("RTB", formatRtbNumber(e.target.value))}
                      placeHolder="RT-1234-12345678"
                      maxLength={16}
                      style={{
                        label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                        input: {
                          height: 50,
                          border: "1px solid #F1EFFB",
                          padding: 8,
                          background: "#F1EFFB",
                          borderRadius: 6,
                          width: "100%",
                        },
                        container: { display: "flex", flexDirection: "column", gap: 6 },
                      }}
                      label="RTB Number"
                    />
                     <ErrorMessage name="RTB">
                       {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                     </ErrorMessage>
                  </div>
                )}
              </div>
            </div>

            {/* LANDLORD COMPANY DETAILS SECTION */}
            {values.userRole === "LANDLORD" && values.accountType !== "INDIVIDUAL" && (
              <div
                className="add-user-card"
                style={{
                  backgroundColor: "white",
                  padding: 20,
                  borderRadius: 10,
                  border: "1px solid #F1EFFB",
                  marginTop: 10,
                  width: "100%",
                  maxWidth: 836,
                }}
              >
                <Typography fontWeight="medium" size={"h3"} style={{ marginBottom: 12 }}>
                  Company Details
                </Typography>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                  <div>
                    <LabelInputField
                      name="companyName"
                      onChange={(e) => setFieldValue("companyName", e.target.value)}
                      placeHolder="Company legal name"
                      style={{
                        label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                        input: { height: 50, border: "1px solid #F1EFFB", padding: 8, background: "#F1EFFB", borderRadius: 6, width: "100%" },
                        container: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 10 },
                      }}
                      label="Company Name"
                    />
                     <ErrorMessage name="companyName">
                       {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                     </ErrorMessage>
                  </div>

                  <div>
                    <LabelInputField
                      name="croNumber"
                      onChange={(e) => setFieldValue("croNumber", e.target.value)}
                      placeHolder="CRO Reg number"
                      style={{
                        label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                        input: { height: 50, border: "1px solid #F1EFFB", padding: 8, background: "#F1EFFB", borderRadius: 6, width: "100%" },
                        container: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 10 },
                      }}
                      label="CRO Number"
                    />
                     <ErrorMessage name="croNumber">
                       {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                     </ErrorMessage>
                  </div>

                  <div>
                    <LabelInputField
                      name="registeredAddress"
                      onChange={(e) => setFieldValue("registeredAddress", e.target.value)}
                      placeHolder="Official registered address"
                      style={{
                        label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                        input: { height: 50, border: "1px solid #F1EFFB", padding: 8, background: "#F1EFFB", borderRadius: 6, width: "100%" },
                        container: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 10 },
                      }}
                      label="Registered Address"
                    />
                     <ErrorMessage name="registeredAddress">
                       {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                     </ErrorMessage>
                  </div>

                  <div>
                    <LabelInputField
                      name="companyWebsite"
                      onChange={(e) => setFieldValue("companyWebsite", e.target.value)}
                      placeHolder="https://example.com"
                      style={{
                        label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                        input: { height: 50, border: "1px solid #F1EFFB", padding: 8, background: "#F1EFFB", borderRadius: 6, width: "100%" },
                        container: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 10 },
                      }}
                      label="Company Website"
                    />
                     <ErrorMessage name="companyWebsite">
                       {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                     </ErrorMessage>
                  </div>
                </div>

                <Typography fontWeight="medium" size={"h4"} style={{ marginTop: 24, marginBottom: 8, color: "#555A77" }}>
                  Primary Contact Person
                </Typography>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
                  <div>
                    <LabelInputField
                      name="companyContactName"
                      onChange={(e) => setFieldValue("companyContactName", e.target.value)}
                      placeHolder="Contact full name"
                      style={{
                        label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                        input: { height: 50, border: "1px solid #F1EFFB", padding: 8, background: "#F1EFFB", borderRadius: 6, width: "100%" },
                        container: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 10 },
                      }}
                      label="Contact Name"
                    />
                     <ErrorMessage name="companyContactName">
                       {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                     </ErrorMessage>
                  </div>

                  <div>
                    <LabelInputField
                      name="companyContactEmail"
                      onChange={(e) => setFieldValue("companyContactEmail", e.target.value)}
                      placeHolder="Contact email"
                      style={{
                        label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                        input: { height: 50, border: "1px solid #F1EFFB", padding: 8, background: "#F1EFFB", borderRadius: 6, width: "100%" },
                        container: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 10 },
                      }}
                      label="Contact Email"
                    />
                     <ErrorMessage name="companyContactEmail">
                       {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                     </ErrorMessage>
                  </div>

                  <div>
                    <LabelInputField
                      name="companyContactPhone"
                      value={values.companyContactPhone}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, "");
                        if (value.length > 15) {
                          value = value.slice(0, 15);
                        }
                        setFieldValue("companyContactPhone", value);
                      }}
                      placeHolder="Contact phone number"
                      maxLength={15}
                      style={{
                        label: { fontSize: "14px", fontWeight: "500", color: "#555A77" },
                        input: { height: 50, border: "1px solid #F1EFFB", padding: 8, background: "#F1EFFB", borderRadius: 6, width: "100%" },
                        container: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 10 },
                      }}
                      label="Contact Phone"
                    />
                     <ErrorMessage name="companyContactPhone">
                       {(msg) => <div style={{ color: "red", fontSize: 12 }}>{msg}</div>}
                     </ErrorMessage>
                  </div>
                </div>
              </div>
            )}

            <div style={{ paddingTop: 20 }}>
              <Button
                type="submit"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                style={{
                  backgroundColor: "#0A56AF",
                  borderRadius: 8,
                  color: "white",
                  padding: "12px 24px",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Submit
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Wrapper>
  );
};

export default AddUser;
